/**
 * Document settings shared between the backend and the tenant/admin
 * frontends. These constants govern the **official** language and
 * timezone used to render backend-generated content (exports, PDFs,
 * import templates, notifications, emails).
 *
 * They are independent of each user's UI language preference. The UI
 * language switcher continues to live in the tenant app and controls
 * only the user's own interface.
 */
import {
  AccountingTimezone,
  DEFAULT_ACCOUNTING_TIMEZONE,
  isSupportedTimezone,
} from "../period/timezone";


/**
 * BCP 47 codes the application currently produces content in. Kinyarwanda
 * (`rw`) is a selectable document language, but backend-generated content
 * (notifications, receipts, emails, exports, import templates) renders in
 * English until Kinyarwanda translations are authored — each rendering
 * surface resolves `rw` to English in its own normaliser.
 */
export const DOCUMENT_LANGUAGE_VALUES = ["fr", "en", "rw"] as const;
export type DocumentLanguage = (typeof DOCUMENT_LANGUAGE_VALUES)[number];

/**
 * The timezones the application supports, in UI-dropdown order (Africa first — the primary market).
 *
 * DERIVED from `AccountingTimezone`, deliberately. This list used to be its own hand-maintained array of the
 * same 16 zones, which is a list that drifts: add a zone here and the accounting calendar rejects it; add it
 * there and the dropdown never offers it. A timezone is no longer a document-formatting preference — since
 * IBA-118 it decides when the accounting month begins — so there is exactly one supported set, and this is a
 * view onto it.
 */
export const DOCUMENT_TIMEZONE_VALUES = Object.values(AccountingTimezone) as [
  AccountingTimezone,
  ...AccountingTimezone[],
];
export type DocumentTimezone = AccountingTimezone;

/** Fallback when stored values are missing (legacy data) or invalid. */
export const DEFAULT_DOCUMENT_LANGUAGE: DocumentLanguage = "fr";
export const DEFAULT_DOCUMENT_TIMEZONE: DocumentTimezone = DEFAULT_ACCOUNTING_TIMEZONE;

/**
 * `true` when the supplied value is one of the curated IANA zones.
 * The backend uses this as a soft validator; persistence is the
 * source of truth so any non-curated string already in the database
 * still resolves through `Intl.DateTimeFormat` correctly.
 */
export const isCuratedDocumentTimezone = (value: unknown): value is DocumentTimezone =>
  typeof value === "string" && isSupportedTimezone(value);

export const isDocumentLanguage = (value: unknown): value is DocumentLanguage =>
  typeof value === "string" &&
  (DOCUMENT_LANGUAGE_VALUES as readonly string[]).includes(value);

/**
 * Resolve a stored value to a guaranteed-valid `DocumentLanguage`,
 * falling back to the application default if missing/unknown.
 */
export const coerceDocumentLanguage = (raw: unknown): DocumentLanguage =>
  isDocumentLanguage(raw) ? raw : DEFAULT_DOCUMENT_LANGUAGE;

export const coerceDocumentTimezone = (raw: unknown): string =>
  typeof raw === "string" && raw.length > 0 ? raw : DEFAULT_DOCUMENT_TIMEZONE;
